import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './addedPropertiesTable.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import { getPerformanceAddedPropertiesTable } from '@/store/slices/components/dashboard/service';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import AddedPropertiesTableRow from './AddedPropertiesTableRow';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function AddedPropertiesTable() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { addedProperties } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const headers = [
    {
      id: 1,
      title: Trans(translations, 'Property'),
      subtitle: Trans(translations, 'Location'),
      field: TABLE_SORT_FIELDS.PROPERTY_ADDRESS,
    },
    {
      id: 2,
      title: Trans(translations, 'Operation'),
      subtitle: Trans(translations, 'PropertyType'),
      field: TABLE_SORT_FIELDS.OPERATION,
    },
    {
      id: 3,
      title: Trans(translations, 'Value'),
      field: TABLE_SORT_FIELDS.OPERATION_AMOUNT,
    },

    {
      id: 4,
      title: Trans(translations, 'Owner'),
      field: TABLE_SORT_FIELDS.OWNER_NAME,
    },
    {
      id: 5,
      title: Trans(translations, 'Agent'),
      subtitle: Trans(translations, 'Branch'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 6,
      title: Trans(translations, 'EntryDate'),
      field: TABLE_SORT_FIELDS.ENTRY_DATE,
    },
  ];

  useEffect(() => {
    if (!addedProperties.has_next) return;
    if (!isInViewport || addedProperties.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(addedProperties, {
      withNextPage: true,
    });

    dispatch(getPerformanceAddedPropertiesTable({ query, tableQuery }));
  }, [isInViewport]);

  const handleCellClick = (header) => {
    dispatch(
      setPerformanceModals({
        key: 'addedProperties',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.addedProperties(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      id="added-properties-table"
      className={`${parentStyles.table} ${specificStyles.addedPropertiesTable}`}
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={addedProperties.sortBy}
            sortDirection={addedProperties.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {addedProperties.list.map((addedProperty) => (
          <AddedPropertiesTableRow
            key={addedProperty.id}
            addedProperty={addedProperty}
          />
        ))}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(addedProperties.loading || addedProperties.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
